import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, MapPin, Power, PowerOff } from 'lucide-react';
import { FormFeed } from '../../../services/formFeedsApi';
import { motion } from 'framer-motion';
import Badge from '../../../components/ui/Badge';
import { feedStatusVariant } from '../../../utils/badgeVariants';

interface FeedListProps {
  feeds: FormFeed[];
  onDelete: (id: number) => void;
}

const FeedList: React.FC<FeedListProps> = ({ feeds, onDelete }) => {
  const navigate = useNavigate();

  const handleDelete = (id: number, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      onDelete(id);
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full ">
        <thead className="theme-bg-tertiary border-b theme-border">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-semibold theme-text-primary">Feed Name</th>
            <th className="px-4 py-3 text-left text-sm font-semibold theme-text-primary">Form</th>
            <th className="px-4 py-3 text-left text-sm font-semibold theme-text-primary">Target Module</th>
            <th className="px-4 py-3 text-left text-sm font-semibold theme-text-primary">Status</th>
            <th className="px-4 py-3 text-right text-sm font-semibold theme-text-primary">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y theme-border">
          {feeds.map((feed, index) => (
            <motion.tr
              key={feed.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="theme-bg-primary hover:theme-bg-tertiary transition-colors"
            >
              <td className="px-4 py-3 text-sm theme-text-primary font-medium">{feed.name}</td>
              <td className="px-4 py-3 text-sm theme-text-secondary">
                {feed.form?.title || `Form #${feed.form_id}`}
              </td>
              <td className="px-4 py-3 text-sm theme-text-secondary capitalize">
                {feed.target_module.replace('-', ' ')}
              </td>
              <td className="px-4 py-3">
                <Badge variant={feedStatusVariant(feed.is_active)}>
                  {feed.is_active ? <><Power className="w-3 h-3 inline mr-1" />Active</> : <><PowerOff className="w-3 h-3 inline mr-1" />Inactive</>}
                </Badge>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => navigate(`/form-feeds/mapping/${feed.form_id}?feedId=${feed.id}`)}
                    className="p-2 rounded-lg theme-bg-tertiary hover:bg-blue-100 dark:hover:bg-blue-900/30 theme-text-secondary hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    title="View Mapping"
                  >
                    <MapPin className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => navigate(`/form-feeds/edit/${feed.id}`)}
                    className="p-2 rounded-lg theme-bg-tertiary hover:bg-blue-100 dark:hover:bg-blue-900/30 theme-text-secondary hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(feed.id, feed.name)}
                    className="p-2 rounded-lg theme-bg-tertiary hover:bg-red-100 dark:hover:bg-red-900/30 theme-text-secondary hover:text-red-600 dark:hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
      {feeds.length === 0 && (
        <div className="text-center py-12 theme-text-tertiary">
          <p>No feeds found. Create your first feed to get started.</p>
        </div>
      )}
    </div>
  );
};

export default FeedList;
